/**
 * O que precisa de Publicar e o que não precisa.
 *
 * Esta distinção existe no sistema desde o começo, mas **nunca apareceu na tela** — e o sintoma foi
 * relatado como defeito: "adicionei um domínio e o botão de publicar não fica disponível". O botão
 * estava certo; a interface é que não contava por quê.
 *
 * A causa é onde cada campo mora no documento do webchat:
 *
 * - `published.appearance` e `published.flow` são o **par draft/published**. O `getConfig` só serve o
 *   que está em `published`, então editar o rascunho não muda nada no site do cliente até publicar.
 *   É a garantia central do produto: ninguém quebra um site no meio de uma edição.
 * - `authorizedDomains`, `outboundAllowlist`, `collectLeads`, `conversationLimit` e o nome moram na
 *   **raiz** do documento, e o servidor os lê de lá direto (ver `handleGetConfig`). Não existe versão
 *   deles, então o autosave já é a publicação — no prazo do cache da config (ver `cache.ts` no engine).
 *
 * Não é inconsistência, é decisão: um domínio autorizado é uma chave de acesso, não conteúdo. Se
 * revogar um domínio exigisse publicar, revogar um acesso indevido ficaria preso a um fluxo que talvez
 * nem esteja publicável naquele momento — e o mesmo vale para baixar o teto de gastos quando a conta
 * está subindo.
 *
 * `sameVersionContent` (no engine) é quem materializa isso: compara **só** `appearance` e `flow`, e é
 * a mesma função que decide se o botão Publicar tem o que fazer.
 */

import { CONFIG_PROPAGATION_SECONDS } from '@merlin/flow-engine';
import { Badge } from '../ui.js';

/**
 * Selo do cabeçalho da seção.
 *
 * `versionado` = o campo vive no par draft/published e precisa de Publicar. O tom não é decorativo:
 * âmbar é "atenção, falta um passo", verde é "já está feito".
 */
export function SeloPropagacao({ versionado }: { versionado: boolean }) {
  return versionado ? (
    <Badge tone="warn">precisa publicar</Badge>
  ) : (
    <Badge tone="ok">vale ao salvar</Badge>
  );
}

/** A frase que explica o selo verde, para a seção não depender de o usuário passar o mouse nele. */
export function ValeAoSalvar({ oQue }: { oQue: string }) {
  return (
    <>
      {oQue} não passa por publicação: o servidor lê direto do webchat, então vale no próximo
      salvamento automático — em até {CONFIG_PROPAGATION_SECONDS}s no site do cliente, o tempo do cache
      da configuração. O botão <strong>Publicar</strong> continuar apagado aqui é o esperado.
    </>
  );
}
